import { useEffect, useRef, useState } from 'react';
import { drive, archive, rag, type ArchiveIngestResult, type RagSearchHit, type BulkReindexSummary, type DriveRemoteFile } from '@/api/endpoints';
import type { ArchiveDocument, Client } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { readDroppedFiles, isSystemJunk } from '@/components/dropFiles';
import { validateUploadSize, MAX_UPLOAD_BYTES } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

interface Stats {
  totalFiles?: number;
  indexedFiles?: number;
  filesToIndex?: number;
  percent?: number;
  mode?: string;
  rootFolder?: string;
}

export function Drive() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [files, setFiles] = useState<ArchiveDocument[] | null>(null);
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Osobny input z `webkitdirectory` — pozwala wybrać CAŁY folder (inaczej niż drag&drop,
  // które samo rekursywnie czyta drzewo). Trzymamy dwa inputy bo `webkitdirectory`
  // wymusza wybór folderu i odbiera użytkownikowi możliwość wybrania pojedynczych plików.
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploadClientId, setUploadClientId] = useState<number | ''>('');
  const [uploadYear, setUploadYear] = useState<number | ''>(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<ArchiveIngestResult[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // Feedback dla dużych folderów — skanowanie drzewa katalogów i postęp uploadu.
  const [scanning, setScanning] = useState(false);
  const [scannedCount, setScannedCount] = useState(0);
  const [uploadIdx, setUploadIdx] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [currentFile, setCurrentFile] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);   // null = brak wysyłki (postęp bajtowy bieżącego pliku)

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchHits, setSearchHits] = useState<RagSearchHit[] | null>(null);

  // Reindex state — flagi rozróżniają „pełny reindeks" (kosztowny, obejmuje już
  // zaindeksowane) od „doindeksowania zaległych" (tylko indexed=false).
  const [reindexAllBusy, setReindexAllBusy] = useState(false);
  const [reindexPendingBusy, setReindexPendingBusy] = useState(false);
  const [reindexAllResult, setReindexAllResult] = useState<BulkReindexSummary | null>(null);

  // Drive remote state
  const [remoteQuery, setRemoteQuery] = useState('');
  const [remoteFolderId, setRemoteFolderId] = useState('');
  const [remoteFiles, setRemoteFiles] = useState<DriveRemoteFile[] | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteImporting, setRemoteImporting] = useState<string | null>(null);
  const [remoteImported, setRemoteImported] = useState<Record<string, ArchiveIngestResult | { error: string }>>({});

  // Bulk import state (async z progress polling)
  const [bulkJobId, setBulkJobId] = useState<string | null>(null);
  const [bulkStatus, setBulkStatus] = useState<{
    state: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
    stage: string;
    totalFound: number;
    processed: number;
    imported: number;
    skipped: number;
    failed: number;
    totalFragments: number;
    currentFile: string | null;
    error: string | null;
    details?: { fileId: string; name: string; mimeType: string; status: string; fragments: number; error: string | null }[];
  } | null>(null);
  const bulkActive = bulkStatus?.state === 'PENDING' || bulkStatus?.state === 'RUNNING';

  const reloadFiles = () => {
    setFiles(null);
    drive.files(clientId).then(setFiles).catch(e => setErr(String(e)));
  };

  const reloadStats = () => {
    drive.stats().then(s => setStats(s as Stats)).catch(() => {});
  };

  useEffect(() => {
    Promise.all([drive.stats(), drive.clients()])
      .then(([s, k]) => { setStats(s as Stats); setClients(k); })
      .catch(e => setErr(String(e)));
  }, []);

  useEffect(() => { reloadFiles(); /* eslint-disable-next-line */ }, [clientId]);

  const onIndex = (id: number) => {
    setBusy(id);
    archive.reindex(id)
      .then(r => {
        if (!r.success) setErr(`Reindex #${id}: ${r.error ?? 'błąd'}`);
        reloadFiles(); reloadStats();
      })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(null));
  };

  const onReindexAll = () => {
    if (!confirm('Reindeksować wszystkie dokumenty? To pobierze pliki z MinIO, wyciągnie tekst i przeliczy embeddingi (może chwilę potrwać).')) return;
    setReindexAllBusy(true); setReindexAllResult(null); setErr(null);
    archive.reindexAll()
      .then(r => { setReindexAllResult(r); reloadFiles(); reloadStats(); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setReindexAllBusy(false));
  };

  /** Doindeksowanie tylko brakujących (indexed=false) — tańsze od pełnego reindeksu. */
  const onReindexPending = () => {
    const pending = stats?.filesToIndex ?? null;
    const msg = pending != null && pending > 0
      ? `Zaindeksować ${pending} ${pending === 1 ? 'brakujący' : 'brakujących'} dokument${pending === 1 ? '' : 'ów'}? Pomijamy już zaindeksowane.`
      : 'Zaindeksować dokumenty oznaczone jako brakujące (indexed=false)?';
    if (!confirm(msg)) return;
    setReindexPendingBusy(true); setReindexAllResult(null); setErr(null);
    archive.reindexPending()
      .then(r => { setReindexAllResult(r); reloadFiles(); reloadStats(); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setReindexPendingBusy(false));
  };

  const loadRemote = (folder?: string) => {
    setRemoteLoading(true); setRemoteFiles(null); setErr(null);
    drive.listRemote(remoteQuery || undefined, (folder ?? remoteFolderId) || undefined)
      .then(setRemoteFiles)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setRemoteLoading(false));
  };

  const importRemote = (f: DriveRemoteFile) => {
    setRemoteImporting(f.id);
    drive.importRemote({
      fileId: f.id, name: f.name, mimeType: f.mimeType,
      clientId: uploadClientId === '' ? undefined : uploadClientId,
      year: uploadYear === '' ? undefined : uploadYear,
    })
      .then(r => {
        setRemoteImported(prev => ({ ...prev, [f.id]: r }));
        reloadFiles(); reloadStats();
      })
      .catch(e => {
        setRemoteImported(prev => ({ ...prev, [f.id]: { error: e?.message ?? String(e) } }));
      })
      .finally(() => setRemoteImporting(null));
  };

  const importAll = () => {
    if (!confirm(
      `Zaimportować WSZYSTKIE pliki${remoteFolderId ? ' z tego folderu (recursywnie)' : ' widoczne dla SA'}?\n\n` +
      `Job uruchamia się asynchronicznie. Postęp widać w panelu poniżej. Możesz anulować w trakcie.`
    )) return;
    setBulkStatus(null); setErr(null);
    drive.importAllStart({
      rootFolderId: remoteFolderId || undefined,
      defaultClientId: uploadClientId === '' ? undefined : uploadClientId,
      defaultYear: uploadYear === '' ? undefined : uploadYear,
      maxFiles: 500,
    })
      .then(r => {
        setBulkJobId(r.jobId);
        setBulkStatus({
          state: 'PENDING', stage: 'preparing', totalFound: 0, processed: 0,
          imported: 0, skipped: 0, failed: 0, totalFragments: 0, currentFile: null, error: null,
        });
      })
      .catch(e => setErr(e?.message ?? String(e)));
  };

  const cancelImport = () => {
    if (!bulkJobId) return;
    drive.importAllCancel(bulkJobId).catch(e => setErr(e?.message ?? String(e)));
  };

  // Polling status co 1.5s gdy job aktywny
  useEffect(() => {
    if (!bulkJobId || !bulkActive) return;
    const interval = setInterval(() => {
      drive.importAllStatus(bulkJobId)
        .then(s => {
          if (!s.found) return;
          setBulkStatus({
            state: s.state, stage: s.stage,
            totalFound: s.totalFound, processed: s.processed,
            imported: s.imported, skipped: s.skipped, failed: s.failed,
            totalFragments: s.totalFragments, currentFile: s.currentFile, error: s.error,
            details: s.details,
          });
          if (s.state === 'DONE' || s.state === 'FAILED' || s.state === 'CANCELLED') {
            reloadFiles(); reloadStats();
          }
        })
        .catch(() => {});
    }, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJobId, bulkActive]);

  const handleUpload = async (selected: FileList | File[]) => {
    // Filtrujemy śmieci systemowe (.DS_Store, Thumbs.db, ._foo) — szczególnie istotne
    // gdy user upuścił folder, w którym macOS/Windows zostawiły metadane.
    const raw = Array.from(selected).filter(f => !isSystemJunk(f.name));
    if (raw.length === 0) return;
    // Limit produktowy 100 MB/plik — za duże pliki tylko pomijamy (wgrywamy resztę paczki),
    // pokazując gotowy komunikat w istniejącym stanie błędu.
    const sizeErr = validateUploadSize(raw);
    const list = sizeErr ? raw.filter(f => f.size <= MAX_UPLOAD_BYTES) : raw;
    if (list.length === 0) { if (sizeErr) setErr(sizeErr); return; }
    setUploading(true); setErr(sizeErr); setUploadResults([]);
    setUploadIdx(0); setUploadTotal(list.length); setCurrentFile(null);
    try {
      const opts = {
        clientId: uploadClientId === '' ? undefined : uploadClientId,
        year: uploadYear === '' ? undefined : uploadYear,
      };
      const results: ArchiveIngestResult[] = [];
      for (let i = 0; i < list.length; i++) {
        const f = list[i];
        setUploadIdx(i + 1);
        setCurrentFile(f.name);
        setUploadPct(0);   // start postępu bajtowego bieżącego pliku
        try {
          const r = await archive.upload(f, opts, p => setUploadPct(p.percent));
          results.push(r);
        } catch (e: any) {
          results.push({ documentId: null, name: f.name, extractedChars: 0, extractionMethod: 'error: ' + (e?.message ?? e), fragmentsCreated: 0 });
        }
        setUploadPct(null);   // reset między plikami
        setUploadResults([...results]);
      }
      reloadFiles();
      reloadStats();
    } finally {
      setUploading(false);
      setCurrentFile(null);
      setUploadPct(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (uploading || scanning) return;  // ignoruj kolejne dropy podczas pracy
    // Faza 1: skanowanie drzewa katalogów (rekursywnie). Dla dużego folderu trwa
    // kilka–kilkadziesiąt sekund — dlatego pokazujemy licznik na bieżąco.
    setScanning(true);
    setScannedCount(0);
    setErr(null);
    setUploadResults([]);
    let files: File[] = [];
    try {
      files = await readDroppedFiles(e.dataTransfer, n => setScannedCount(n));
    } catch (err: any) {
      setErr('Błąd odczytu folderu: ' + (err?.message ?? err));
    } finally {
      setScanning(false);
    }
    if (files.length > 0) void handleUpload(files);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true); setSearchHits(null);
    rag.search(searchQuery.trim(), 10)
      .then(setSearchHits)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setSearching(false));
  };

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Archiwum · RAG</h1>
          <div className="text-ink-400 text-[13px]">Dokumenty historyczne jako baza wiedzy AI (OCR + embedding + wyszukiwanie semantyczne)</div>
        </div>
        <div className="flex gap-2">
          <button onClick={onReindexPending} disabled={reindexPendingBusy || reindexAllBusy}
            className="btn btn-primary btn-sm"
            title={'Indeksuje TYLKO dokumenty oznaczone jako jeszcze nieindeksowane (indexed=false). '
                 + 'Pomija już zaindeksowane — szybciej i taniej niż pełny reindeks.'}>
            <Icon name="sparkles" className="w-3.5 h-3.5"/>
            {reindexPendingBusy
              ? 'Indeksuję brakujące…'
              : (stats?.filesToIndex && stats.filesToIndex > 0
                  ? `Indeksuj brakujące (${stats.filesToIndex})`
                  : 'Indeksuj brakujące')}
          </button>
          <button onClick={onReindexAll} disabled={reindexAllBusy || reindexPendingBusy}
            className="btn btn-secondary btn-sm border-ai-500/40 text-ai-700"
            title="Pełny reindeks: pobiera każdy plik z MinIO, ekstraktuje tekst od nowa i przelicza embeddingi. Kosztowne — używaj gdy zmieniła się logika ekstrakcji albo model embeddingów.">
            <Icon name="sparkles" className="w-3.5 h-3.5"/>
            {reindexAllBusy ? 'Reindeksuję wszystko…' : 'Reindeksuj wszystkie'}
          </button>
        </div>
      </header>

      {reindexAllResult && (
        <div className="mb-5 card border-water-500/30">
          <div className="card-body">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[13px] font-semibold text-ink-900">
                Bulk reindex: {reindexAllResult.success} / {reindexAllResult.total} OK
                {reindexAllResult.failed > 0 && <span className="text-red-500"> · {reindexAllResult.failed} błędów</span>}
              </div>
              <div className="text-[12px] text-ink-500">
                Łącznie {reindexAllResult.totalFragments} fragmentów wygenerowanych
              </div>
            </div>
            <div className="max-h-[200px] overflow-auto border border-ink-100 rounded">
              <table className="table-hw">
                <thead><tr><th>Plik</th><th className="w-32">Metoda</th><th className="w-20 text-right">Znaki</th><th className="w-20 text-right">Fragm.</th><th className="w-32">Status</th></tr></thead>
                <tbody>
                  {reindexAllResult.results.map((r, i) => (
                    <tr key={i}>
                      <td className="text-[12px] truncate max-w-[300px]">{r.name ?? '?'}</td>
                      <td className="text-[11px] font-mono text-ink-500">{r.extractionMethod}</td>
                      <td className="text-[11px] font-mono text-right">{r.extractedChars}</td>
                      <td className="text-[11px] font-mono text-right">{r.fragmentsCreated}</td>
                      <td>
                        {r.success
                          ? <span className="text-[10px] text-green-700">✓ OK</span>
                          : <span className="text-[10px] text-red-500" title={r.error ?? ''}>✗ {r.error?.slice(0, 30)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {err && <div className="text-red-500 mb-3 text-[12px] p-2 bg-red-50 border border-red-200 rounded">Błąd: {err}</div>}

      {/* KPI */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        <Kpi label="Wszystkie dokumenty" value={stats?.totalFiles ?? '—'} />
        <Kpi label="W bazie RAG" value={stats?.indexedFiles ?? '—'} sub={(stats?.percent ?? 0) + '%'}/>
        <Kpi label="Do zaindeksowania" value={stats?.filesToIndex ?? '—'}/>
        <Kpi label="Tryb" value={stats?.mode ?? '—'} mono/>
      </div>

      {/* Search */}
      <div className="card mb-5">
        <div className="card-head">
          <div className="card-title flex items-center gap-2">
            <Icon name="search"/> Wyszukiwanie semantyczne
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={onSearch} className="flex gap-2">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="np. operat wodnoprawny dla gminy w pasie nadmorskim"
              className="input flex-1"
            />
            <button type="submit" disabled={searching || !searchQuery.trim()} className="btn btn-primary">
              {searching ? 'Szukam…' : 'Szukaj'}
            </button>
          </form>
          {searchHits && searchHits.length === 0 && (
            <div className="text-ink-400 text-[12px] mt-3">Brak wyników. Najpierw wgraj dokumenty do archiwum (panel obok).</div>
          )}
          {searchHits && searchHits.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 max-h-[400px] overflow-auto">
              {searchHits.map(h => (
                <div key={h.fragmentId} className="border border-ink-100 rounded p-3 hover:border-water-500/40 transition">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[12px] font-semibold text-ink-900">
                      {h.documentName ?? 'Dokument #' + h.documentId} · fragm. {h.fragmentNumber}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-ink-400 font-mono">{h.embeddingModel}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-water-100 text-water-700">
                        {(h.score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-[12px] text-ink-700 whitespace-pre-wrap">{h.snippet}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drive Remote — pliki na Google Drive */}
      <div className="card mb-5">
        <div className="card-head">
          <div className="card-title flex items-center gap-2">
            <Icon name="cloud"/> Pliki na Google Drive
          </div>
          <div className="text-[11px] text-ink-400 ml-auto">tryb: {stats?.mode ?? '—'}</div>
        </div>
        <div className="card-body">
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              value={remoteQuery}
              onChange={e => setRemoteQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadRemote()}
              placeholder="Szukaj po nazwie pliku (Drive query)"
              className="input flex-1 min-w-[200px]"
            />
            <input
              value={remoteFolderId}
              onChange={e => setRemoteFolderId(e.target.value)}
              placeholder="ID folderu (opcjonalnie)"
              className="input w-[180px] font-mono text-[11px]"
            />
            <button onClick={() => loadRemote()} disabled={remoteLoading}
              className="btn btn-primary btn-sm">
              {remoteLoading ? 'Ładowanie…' : 'Pokaż pliki z Drive'}
            </button>
            <button onClick={importAll} disabled={bulkActive}
              className="btn btn-sm border-ai-500/40 text-ai-700 hover:bg-ai-500/10">
              <Icon name="sparkles" className="w-3.5 h-3.5"/>
              {bulkActive ? 'Job w toku…' : 'Importuj wszystkie do RAG'}
            </button>
          </div>

          {bulkStatus && (
            <BulkImportPanel
              status={bulkStatus}
              onCancel={cancelImport}
              onClose={() => { setBulkStatus(null); setBulkJobId(null); }}
            />
          )}
          {!remoteFiles && !remoteLoading && (
            <div className="text-ink-400 text-[12px] py-2">
              Kliknij <strong>Pokaż pliki z Drive</strong> aby zobaczyć pliki które Service Account widzi (foldery udostępnione).
            </div>
          )}
          {remoteFiles && remoteFiles.length === 0 && (
            <div className="text-amber-700 text-[12px] py-2 px-3 bg-amber-50 border border-amber-200 rounded">
              Brak plików widocznych przez Service Account.
              Udostępnij folder Drive Service Accountowi (kliknij prawym → Udostępnij → wpisz client_email).
            </div>
          )}
          {remoteFiles && remoteFiles.length > 0 && (
            <div className="border border-ink-100 rounded max-h-[400px] overflow-auto">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th className="w-32">Typ</th>
                    <th className="w-24 text-right">Rozmiar</th>
                    <th className="w-32">Zmodyfikowano</th>
                    <th className="w-36 text-right">Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {remoteFiles.map(f => {
                    const imported = remoteImported[f.id];
                    const sizeKb = f.size ? Math.round(f.size / 1024) : null;
                    const isFolder = f.folder;
                    return (
                      <tr key={f.id} className="hover:bg-ink-50">
                        <td>
                          <div className="flex items-center gap-2">
                            <Icon name={isFolder ? 'home' : 'file-text'} className="w-3.5 h-3.5 text-ink-400"/>
                            <div>
                              {isFolder
                                ? <button onClick={() => { setRemoteFolderId(f.id); loadRemote(f.id); }}
                                    className="font-semibold text-water-600 hover:text-water-700 text-[13px]">
                                    {f.name}
                                  </button>
                                : <span className="font-semibold text-ink-900 text-[13px]">{f.name}</span>}
                              {f.webViewLink && !isFolder && (
                                <a href={f.webViewLink} target="_blank" rel="noreferrer"
                                  className="ml-2 text-[10px] text-ink-400 hover:text-ink-600">↗</a>
                              )}
                            </div>
                          </div>
                          <div className="text-[10px] text-ink-400 font-mono ml-5">{f.id}</div>
                        </td>
                        <td className="text-[11px] font-mono text-ink-600">
                          {f.mimeType.replace('application/vnd.google-apps.', 'gdoc:').replace('application/', '')}
                        </td>
                        <td className="text-right text-[12px] font-mono text-ink-500">
                          {sizeKb ? sizeKb + ' kB' : '—'}
                        </td>
                        <td className="text-[11px] text-ink-500 font-mono">
                          {f.modifiedTime ? f.modifiedTime.slice(0, 10) : '—'}
                        </td>
                        <td className="text-right">
                          {!isFolder && !imported && (
                            <button onClick={() => importRemote(f)} disabled={remoteImporting === f.id}
                              className="btn btn-secondary btn-sm">
                              {remoteImporting === f.id ? '…' : 'Importuj'}
                            </button>
                          )}
                          {imported && 'documentId' in imported && (imported as ArchiveIngestResult).documentId && (
                            <span className="text-[10px] text-green-700">✓ {(imported as ArchiveIngestResult).fragmentsCreated} fragm.</span>
                          )}
                          {imported && 'error' in imported && (
                            <span className="text-[10px] text-red-500" title={imported.error as string}>✗ błąd</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-5">
        {/* Lista dokumentów */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">Dokumenty w archiwum</div>
            <SearchableSelect value={clientId != null ? String(clientId) : ''} onChange={v => setClientId(v ? Number(v) : undefined)} className="max-w-[240px] ml-auto">
              <option value="">Wszyscy klienci</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </SearchableSelect>
          </div>
          <div className="card-body !p-0">
            {!files && <div className="text-ink-400 p-4">Ładowanie…</div>}
            {files && files.length === 0 && (
              <div className="text-ink-400 p-6 text-center">Brak dokumentów. Wgraj pierwszy plik z prawej.</div>
            )}
            {files && files.length > 0 && (
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th className="w-32">Klient</th>
                    <th className="w-20">Rok</th>
                    <th className="w-24">Rozmiar</th>
                    <th className="w-24">Status RAG</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(f => (
                    <tr key={f.id} className="hover:bg-ink-50">
                      <td>
                        <div className="font-semibold text-ink-900 text-[13px]">{f.name}</div>
                        {f.intakeName && <div className="text-[11px] text-ink-400">Ujęcie: {f.intakeName}</div>}
                      </td>
                      <td className="text-[12px] text-ink-700">{f.clientName ?? '—'}</td>
                      <td className="text-[12px] text-ink-500 font-mono">{f.year ?? '—'}</td>
                      <td className="text-[12px] text-ink-500 font-mono">
                        {f.sizeBytes ? Math.round(f.sizeBytes / 1024) + ' kB' : '—'}
                      </td>
                      <td>
                        {f.indexed
                          ? <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">indexed</span>
                          : <button disabled={busy === f.id} onClick={() => onIndex(f.id)} className="btn btn-secondary btn-sm">
                              {busy === f.id ? '…' : 'Indeksuj'}
                            </button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Upload */}
        <div className="card">
          <div className="card-head"><div className="card-title">Wgraj do archiwum</div></div>
          <div className="card-body flex flex-col gap-3">
            <label className="field">
              <span className="field-label">Klient (opcjonalnie)</span>
              <SearchableSelect value={uploadClientId === '' ? '' : String(uploadClientId)} onChange={v => setUploadClientId(v ? Number(v) : '')}>
                <option value="">— bez przypisania —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Rok</span>
              <input type="number" value={uploadYear} onChange={e => setUploadYear(e.target.value ? Number(e.target.value) : '')} className="input font-mono"/>
            </label>

            <div
              onDragOver={e => { e.preventDefault(); if (!uploading && !scanning) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => { if (!uploading && !scanning) fileInputRef.current?.click(); }}
              aria-disabled={uploading || scanning}
              className={'border-2 border-dashed rounded-lg p-5 text-center transition '
                + ((uploading || scanning) ? 'opacity-60 cursor-not-allowed border-ink-200 ' : 'cursor-pointer ')
                + (dragOver ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
              <Icon name="upload" className="w-6 h-6 mx-auto mb-2 text-ink-400"/>
              <div className="text-[13px] text-ink-700">Upuść pliki lub cały folder, albo kliknij</div>
              <div className="text-[10px] text-ink-400 mt-1">PDF · DOCX · DOC · PNG · JPG · TXT</div>
            </div>
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
                className="text-[11px] text-ai-700 hover:text-ai-900 hover:underline inline-flex items-center gap-1"
                title="Wybierz cały katalog z dysku — zostaną wgrane wszystkie pliki rekursywnie">
                <Icon name="folder" className="w-3 h-3"/> …lub wybierz folder z dysku
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.txt"
              onChange={e => e.target.files && handleUpload(e.target.files)}
              className="hidden"
            />
            {/* webkitdirectory — pozwala wybrać cały katalog (Chrome/Edge/Firefox/Safari) */}
            <input
              ref={folderInputRef}
              type="file"
              // @ts-expect-error — webkitdirectory nie jest w lib.dom.d.ts, ale jest standardem de-facto
              webkitdirectory=""
              directory=""
              multiple
              onChange={e => e.target.files && handleUpload(e.target.files)}
              className="hidden"
            />

            {scanning && (
              <div className="rounded border border-water-500/30 bg-water-500/5 px-3 py-2 text-[12px]">
                <div className="flex items-center gap-2 text-water-700 font-semibold">
                  <Icon name="folder" className="w-3.5 h-3.5 animate-pulse"/>
                  Skanuję folder…
                  {scannedCount > 0 && (
                    <span className="ml-auto font-mono text-ink-700">{scannedCount} {scannedCount === 1 ? 'plik' : (scannedCount < 5 ? 'pliki' : 'plików')}</span>
                  )}
                </div>
                <div className="text-[10px] text-ink-500 mt-1">
                  Czytam drzewo katalogów rekursywnie. Dla dużych folderów może to potrwać kilkadziesiąt sekund.
                </div>
              </div>
            )}

            {uploading && uploadTotal > 0 && (
              <div className="rounded border border-ai-500/30 bg-ai-500/5 px-3 py-2 text-[12px]">
                <div className="flex items-center gap-2 text-ai-700 font-semibold">
                  <Icon name="upload" className="w-3.5 h-3.5"/>
                  Wgrywanie {uploadIdx} z {uploadTotal}
                  <span className="ml-auto font-mono text-ink-700">{Math.round((uploadIdx / uploadTotal) * 100)}%</span>
                </div>
                <div className="mt-1.5 h-1.5 bg-ink-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-ai-500 transition-[width] duration-200"
                    style={{ width: `${(uploadIdx / uploadTotal) * 100}%` }}
                  />
                </div>
                {currentFile && (
                  <div className="text-[10px] text-ink-500 mt-1 font-mono truncate" title={currentFile}>
                    Aktualnie: {currentFile}
                  </div>
                )}
              </div>
            )}

            {uploadPct !== null && (
              <UploadProgressBar percent={uploadPct} label={`Wysyłanie: ${currentFile ?? 'plik'}`} compact />
            )}

            {uploadResults.length > 0 && (
              <div className="border-t border-ink-100 pt-2 max-h-[300px] overflow-auto">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-600 mb-1">Wyniki</div>
                {uploadResults.map((r, i) => (
                  <div key={i} className="text-[11px] py-1 border-b border-ink-50 last:border-0">
                    <div className={'font-semibold ' + (r.documentId ? 'text-ink-900' : 'text-red-500')}>
                      {r.documentId ? '✓' : '✗'} {r.name}
                    </div>
                    <div className="text-ink-500 font-mono text-[10px]">
                      {r.extractionMethod} · {r.extractedChars} znaków · {r.fragmentsCreated} fragm.
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-600 mt-2">Pipeline</div>
            <ol className="flex flex-col gap-1 text-[11px] text-ink-600">
              <li>1. Upload do MinIO</li>
              <li>2. Ekstrakcja tekstu (PDFBox / OCR / POI)</li>
              <li>3. Chunking (~800 znaków, overlap 100)</li>
              <li>4. Embedding (Ollama / Gemini)</li>
              <li>5. Zapis fragmentów + dostępne w RAG</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, mono }: { label: string; value: any; sub?: string; mono?: boolean }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{label}</div>
        <div className={'text-[24px] font-bold text-ink-900 mt-1 ' + (mono ? 'font-mono text-[18px]' : '')}>{value}</div>
        {sub && <div className="text-[11px] text-ink-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

interface BulkImportPanelProps {
  status: NonNullable<ReturnType<typeof useState<{
    state: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
    stage: string;
    totalFound: number;
    processed: number;
    imported: number;
    skipped: number;
    failed: number;
    totalFragments: number;
    currentFile: string | null;
    error: string | null;
    details?: { fileId: string; name: string; mimeType: string; status: string; fragments: number; error: string | null }[];
  } | null>>[0]>;
  onCancel: () => void;
  onClose: () => void;
}

function BulkImportPanel({ status, onCancel, onClose }: BulkImportPanelProps) {
  const active = status.state === 'PENDING' || status.state === 'RUNNING';
  const total = Math.max(status.totalFound, status.processed);
  const pct = total > 0 ? Math.round((status.processed / total) * 100) : 0;

  const stateColors: Record<string, string> = {
    PENDING:   'bg-ink-100 text-ink-700',
    RUNNING:   'bg-water-100 text-water-700',
    DONE:      'bg-green-100 text-green-700',
    FAILED:    'bg-red-100 text-red-700',
    CANCELLED: 'bg-amber-100 text-amber-700',
  };
  const stateLabel: Record<string, string> = {
    PENDING:   'Inicjalizacja',
    RUNNING:   'W toku',
    DONE:      'Ukończono',
    FAILED:    'Błąd',
    CANCELLED: 'Anulowano',
  };
  const stageLabel: Record<string, string> = {
    preparing: 'Przygotowywanie',
    listing:   'Listowanie plików z Drive',
    importing: 'Import + embedding',
    done:      'Zakończone',
  };

  return (
    <div className="mb-3 border border-water-500/40 rounded">
      <div className="bg-water-50 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className={'px-2 py-0.5 rounded text-[10px] font-semibold ' + stateColors[status.state]}>
              {stateLabel[status.state] ?? status.state}
            </span>
            <span className="text-[12px] text-ink-600">{stageLabel[status.stage] ?? status.stage}</span>
          </div>
          <div className="flex items-center gap-2">
            {active && (
              <button onClick={onCancel} className="btn btn-secondary btn-sm text-red-600">
                Anuluj
              </button>
            )}
            {!active && (
              <button onClick={onClose} className="btn btn-ghost btn-icon text-ink-400">
                <Icon name="x"/>
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {status.totalFound > 0 && (
          <>
            <div className="flex items-center gap-2 text-[12px] mb-1">
              <span className="font-mono tabular-nums text-ink-700">
                {status.processed} / {status.totalFound}
              </span>
              <span className="text-ink-400">plików</span>
              <span className="ml-auto font-mono text-ink-600">{pct}%</span>
            </div>
            <div className="h-2 bg-ink-100 rounded-full overflow-hidden">
              <div className={'h-full transition-all ' + (active ? 'bg-water-500' : 'bg-green-500')}
                style={{ width: pct + '%' }}/>
            </div>
          </>
        )}

        {status.totalFound === 0 && active && (
          <div className="text-[12px] text-ink-500">Listowanie plików z Drive…</div>
        )}

        {/* Counters */}
        <div className="grid grid-cols-4 gap-2 mt-3 text-[12px]">
          <div className="bg-white rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-400">Zaimportowane</div>
            <div className="text-[18px] font-bold text-green-700 tabular-nums">{status.imported}</div>
          </div>
          <div className="bg-white rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-400">Pominięte</div>
            <div className="text-[18px] font-bold text-ink-600 tabular-nums">{status.skipped}</div>
            <div className="text-[10px] text-ink-400">już w bazie</div>
          </div>
          <div className="bg-white rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-400">Błędy</div>
            <div className={'text-[18px] font-bold tabular-nums ' + (status.failed > 0 ? 'text-red-600' : 'text-ink-300')}>
              {status.failed}
            </div>
          </div>
          <div className="bg-white rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-400">Fragmenty RAG</div>
            <div className="text-[18px] font-bold text-water-700 tabular-nums">{status.totalFragments}</div>
          </div>
        </div>

        {/* Current file */}
        {status.currentFile && active && (
          <div className="mt-3 text-[11px] text-ink-600 bg-white px-2 py-1 rounded font-mono truncate">
            ⚡ Aktualnie: {status.currentFile}
          </div>
        )}

        {status.error && (
          <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
            Błąd: {status.error}
          </div>
        )}
      </div>

      {/* Details (po zakończeniu) */}
      {!active && status.details && status.details.length > 0 && (
        <div className="max-h-[250px] overflow-auto border-t border-water-500/30">
          <table className="table-hw text-[11px]">
            <thead>
              <tr><th>Plik</th><th className="w-24">Status</th><th className="w-20 text-right">Fragm.</th><th>Błąd</th></tr>
            </thead>
            <tbody>
              {status.details
                .filter(d => d.status === 'failed' || d.status === 'imported')
                .slice(0, 50)
                .map(d => (
                  <tr key={d.fileId}>
                    <td className="truncate max-w-[300px]">{d.name}</td>
                    <td>
                      <span className={'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ' +
                        (d.status === 'imported' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                        {d.status}
                      </span>
                    </td>
                    <td className="text-right font-mono">{d.fragments}</td>
                    <td className="text-red-500 text-[10px] truncate max-w-[400px]" title={d.error ?? ''}>{d.error ?? ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
